import { useSessionContext } from '@/contexts/session/context';
import cryptoManager from '@/scripts/crypto';
import { chatApis } from '@/configs/apis/chat';
import type { ChatMessageMessageIdParams } from '../useChatMessages/types';
import { ChatMessageCategory } from '../useChatMessages/types';

export function useSingleEncryption() {
  const targetPubKeys = useRef<Record<string, any>>();

  const {
    session: { user },
  } = useSessionContext();

  const encryptMessage = useCallback((receiverId: string, text: string): string => {
    try {
      return cryptoManager.encryptMessage(receiverId, text);
    } catch (error: any) {
      console.error('encryption failed', error);
      return error?.message;
    }
  }, []);

  const decryptMessage = useCallback((message: ChatMessageMessageIdParams): string => {
    try {
      return cryptoManager.decryptMessage(
        message.category === ChatMessageCategory.SEND
          ? message.receiverId!
          : message.senderId!,
        message.payload!,
        message.category === ChatMessageCategory.SEND
      );
    } catch (error: any) {
      console.error('decryption failed', error);
      return `Decryption failed (${error?.message})`;
    }
  }, []);

  const getTargetChatPubKeys = useCallback((receiverId: string) => {
    return chatApis
      .getTargetKeyPackage(receiverId)
      .then((res) => {
        if (res?.key_package) {
          targetPubKeys.current = {
            ...targetPubKeys.current,
            [receiverId]: {
              keyPackage: res?.key_package,
            },
          };
          cryptoManager.setKeyPackage(receiverId, res?.key_package);
        }
      })
      .catch((_) => {
        console.log('No found user pubKey');
      });
  }, []);

  const initCrypto = async (userId: string) => {
    await cryptoManager.initCrypto(userId);
    const userPackage = await chatApis.getUserPackage({ userId, type: 'single' });
    if (!userPackage) {
      const keyPackage = cryptoManager.getKeyPacakge();
      await chatApis.sendKeyPackage({
        key_package: [keyPackage],
      });
      chatApis.setUserPackage({ userId, type: 'single', keyPackage });
    }
  };

  useLayoutEffect(() => {
    if (user?.userId) {
      initCrypto(user?.userId);
    }
  }, [user]);

  const value = useMemo(
    () => ({
      getTargetChatPubKeys,
      encryptMessage,
      decryptMessage,
    }),
    [getTargetChatPubKeys, encryptMessage, decryptMessage]
  );

  return value;
}
